import {
  DocumentSnapshot,
  FirestoreDataConverter,
  SnapshotOptions,
  WithFieldValue,
} from 'firebase/firestore'
import { CheckbookEntry, CheckbookEntryType } from '../checkbook'

const safeNumber = (value: unknown): number => {
  if (value === undefined || value === null) return 0
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

const safeDate = (value: unknown): string | null => {
  if (!value) return null
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toISOString()
  }
  if (typeof value === 'string') {
    try {
      const d = new Date(value)
      return Number.isFinite(d.getTime()) ? d.toISOString() : null
    } catch {
      return null
    }
  }
  return null
}

const normalizeEntryType = (v: unknown): CheckbookEntryType => {
  if (v === 'deposit' || v === 'withdrawal' || v === 'credit' || v === 'debit') return v
  // Backward compatibility for legacy checkbook rows.
  if (v === 'payment') return 'debit'
  return 'deposit'
}

export const checkbookConverter: FirestoreDataConverter<CheckbookEntry> = {
  toFirestore(entry: WithFieldValue<CheckbookEntry>) {
    const { id, ...data } = entry
    return {
      ...data,
      created_at: data.created_at ?? null,
      created_by: data.created_by ?? '',
      amount: data.amount ?? 0,
      new_total: data.new_total ?? 0,
      type: data.type ?? 'deposit',
      vehicle_id: data.vehicle_id ?? null,
      memo: data.memo ?? null,
    }
  },

  fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions): CheckbookEntry {
    const data = snapshot.data(options)
    if (!data) {
      return {
        id: snapshot.id,
        created_at: null,
        created_by: '',
        amount: 0,
        new_total: 0,
        type: 'deposit',
        vehicle_id: null,
        memo: null,
      }
    }
    return {
      id: snapshot.id,
      created_at: safeDate(data.created_at),
      created_by: typeof data.created_by === 'string' ? data.created_by : '',
      amount: safeNumber(data.amount),
      new_total: safeNumber(data.new_total),
      type: normalizeEntryType(data.type),
      vehicle_id: data.vehicle_id != null ? String(data.vehicle_id) : null,
      memo: typeof data.memo === 'string' && data.memo.trim() ? data.memo.trim() : null,
    }
  },
}
